import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  DialogFooterButtonsWidget,
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  TextLinkKind,
  TextLinkWidget,
  Tooltip,
  TooltipPosition
} from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { MapDispatchToProps } from '~src/commonInfra/utils/ConnectUtils';
import { imgUrl } from '~src/commonInfra/utils/ImgUrl';
import { IDataSourceBase } from '~src/datasource/models/IDataSourceInfo';
import { ContainerType } from '~src/drafts/types/DraftsTypes';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { selectContainerInfo } from '~src/personalSpace/selectors/ContainerInfoSelector';
import { ContainerInfoState } from '~src/personalSpace/state/ContainerInfoState';
import { ConnectionTypes } from '~src/settings/extensions-settings/analytics/AnalyticsExtensionsSiteSection';
import {
  GetAnalyticsConnectionOptionsForWorkbookRequest,
  IGetAnalyticsConnectionOptionsForWorkbookResult
} from '~src/settings/extensions-settings/serverApi/requests/analytics/GetAnalyticsConnectionOptionsForWorkbookRequest';
import {
  IAnalyticsConnectionBrief,
  IAnalyticsConnectionMapping,
  IAnalyticsConnectionMetadata
} from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { shareContent,ShareContentAction } from '~src/sharing/actions/ShareContentAction';
import { ShareContentButton,ShareContentButtonProps } from '~src/sharing/components/namedSharing/ShareContentButton';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { GetHasAnalyticsExtensionTableRequest } from '~src/uploadContent/serverApi/requests/GetHasAnalyticsExtensionTableRequest';
import { GetHasScriptCalcRequest } from '~src/uploadContent/serverApi/requests/GetHasScriptCalcRequest';
import { RemoveAnalyticsExtConnectionForWorkbookRequest } from '~src/workbook/ServerApi/requests/RemoveAnalyticsExtConnectionForWorkbookRequest';
import { SetAnalyticsExtConnectionForWorkbookRequest } from '~src/workbook/ServerApi/requests/SetAnalyticsExtConnectionForWorkbookRequest';

const EINSTEIN_DISCOVERY_CELL_ICON = 'einstein_discovery_new_connection.svg';
const TABPY_CELL_ICON = 'tabpy_new_connection.svg';
const RSERVE_CELL_ICON = 'rserve_new_connection.svg';
const ANALYTICS_EXTENSION_API_CELL_ICON = 'analytics_extensions_api.svg';
const NO_CONNECTION_ICON = 'extension.svg';

export interface PublishCompleteDialogOwnProps {
  dismiss: () => void;
  close: () => void;
  onDevicePreviewClick: () => void;
  workbook: IContentInfo<'workbook'>;
  datasources: IDataSourceBase[];
  getDatasourceIconClass: (datasource: IDataSourceBase) => string;
  publishLiveQueryInfo: string;
  shouldDisplayDevicePreview: boolean;
  shouldDisplayLiveQueryInfo: boolean;
  shouldDisplayScheduleButton: boolean;
}

export interface PublishCompleteDialogDispatchProps {
  readonly shareContent: (content: IContentInfo) => ShareContentAction;
}

export type PublishCompleteDialogProps = PublishCompleteDialogOwnProps & PublishCompleteDialogDispatchProps;

interface DatasourceProps {
  name: string;
  datasourceIconClass: string;
}

// Function called when analytics extensions dropdown option is changed
function useChangeHandler(connectionList: ListItem[]) {
  const [selectedConnection, setSelectedConnection] = React.useState<ListItem>(null);

  return {
    selectedConnection,
    onChange: React.useCallback((connectionLuid: string) => {
      let selection = connectionList.find(c => c.value === connectionLuid);
      setSelectedConnection(selection);
    }, [connectionList]),
  };
}

function createAnExtDropdownListItem(connection: IAnalyticsConnectionBrief) {

  let icon;
  switch (connection.connection_type) {
    case ConnectionTypes.RSERVE.TAG:
    case ConnectionTypes.RSERVE_HTTP.TAG: {
      icon = RSERVE_CELL_ICON;
      break;
    }
    case ConnectionTypes.TABPY.TAG: {
      icon = TABPY_CELL_ICON;
      break;
    }
    case ConnectionTypes.EINSTEIN.TAG: {
      icon = EINSTEIN_DISCOVERY_CELL_ICON;
      break;
    }
    case ConnectionTypes.GENERIC_API.TAG: {
      icon = ANALYTICS_EXTENSION_API_CELL_ICON;
      break;
    }
    default: {
      icon = NO_CONNECTION_ICON;
      break;
    }
  }

  icon=imgUrl(icon);

  return (
    <div className="publish-complete-an-ext-dropdown-item">
      <div
        style={{
          width: '10%',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundImage: icon
        }}/>
      <div>
        {connection.connection_name}
      </div>
    </div>);
}

function useAnExtConnectionListInitializer(props: PublishCompleteDialogOwnProps & PublishCompleteDialogDispatchProps, getAnalyticsConnections: (workbookLuid: string) => Promise<IAnalyticsConnectionMetadata[]>, usesAnalyticsExtensions: boolean) {
  // Sets up a state for our functional component. In this case, a list of available connection options
  // setAnalyticExtensionsConnectionOptions is a function with signature (IAnalyticsExtConnectionSettings[]) => void
  const [analyticExtensionsConnectionOptions, setAnalyticExtensionsConnectionOptions] = React.useState<IAnalyticsConnectionMetadata[]>(undefined);
  React.useEffect(
    () => {
      // Uses API call to get list of connections available for the published workbook, if workbook uses analytics extension
      if (usesAnalyticsExtensions) {
        getAnalyticsConnections(props.workbook.luid).then(connections => {
          if (connections !== undefined) {
            setAnalyticExtensionsConnectionOptions(connections);
          }
        });
      }
    },
    [getAnalyticsConnections, props.workbook.luid, usesAnalyticsExtensions]
  );

  // Returning connection list, which will only be updated if the result of the underlying API call changes
  return React.useMemo(() => {

    if (analyticExtensionsConnectionOptions === undefined) {
      return [];
    }

    const list = analyticExtensionsConnectionOptions.map(connection => ({
      value: connection.connection_luid,
      content: createAnExtDropdownListItem(connection.connection_brief)
    }));

    const firstListItemMessage = list.length === 0 ?
      Messages.publishWorkbookSuccessDialogAnExt_noConnectionsAvailable() :
      Messages.publishWorkbookSuccessDialogAnExt_noConnection();

    list.unshift({value: 'none', content: <div>{firstListItemMessage}</div>});

    return list;
  }, [analyticExtensionsConnectionOptions]);

}

function useWorkbookUsesAnalyticsExtensionsInitializer(props: PublishCompleteDialogOwnProps & PublishCompleteDialogDispatchProps, getHasScriptCalcs: (workbookLuid: string) => Promise<boolean>, getHasAnalyticsExtensionTable: (workbookLuid: string) => Promise<boolean>) {
  // Sets up a state for our functional component.In this case, a boolean indicating whether workbook UsesAnalyticsExtensions
  // setUsesAnalyticsExtensions is a function with signature (boolean) => void
  const [usesAnalyticsExtensions, setUsesAnalyticsExtensions] = React.useState<boolean>(false);
  React.useEffect(
    () => {
      let hasScriptCalcs = false;
      let hasAnalyticsExtensionsTable = false;
      //Uses API call to get whether workbook has script calcs. Assigns this value to hasScriptCalcs
      getHasScriptCalcs(props.workbook.id).then((result: boolean) => {
        hasScriptCalcs = result;
        getHasAnalyticsExtensionTable(props.workbook.id).then((result: boolean) => {
          hasAnalyticsExtensionsTable = result;
          setUsesAnalyticsExtensions(hasScriptCalcs || hasAnalyticsExtensionsTable);
        });
      });
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  return usesAnalyticsExtensions;
}

export namespace PublishCompleteDialog {
  export function Title(): string {
    return Messages.publishDatasourceSuccessDialogTitleComplete();
  }

  export function Content(props: PublishCompleteDialogProps) {
    return (
      <div className="tb-workbook-publish-success-modal-with-preview">
        <div className="tb-dialog-body-text">
          <div className="tb-published-items">
            <WorkbookSection {...props} />
            <DatasourceSection {...props} />
          </div>
          <div className="tb-connection-section">
            <AnalyticsConnectionSettingsSection {...props} />
          </div>
          <TableauBridgeBox {...props}/>
        </div>
      </div>
    );
  }

  export function WorkbookSection(props: PublishCompleteDialogProps) {
    return (
      <div className="tb-workbook">
        <div className="tb-workbook-body">
          <div className="tb-published-item">
            <div className="tb-workbook-wrapper">
              <div className="tb-published-icon tb-dialogs-workbook" />
              <div className="tb-published-name">{props.workbook.name}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  async function getAnalyticsConnections(workbookLuid: string) {
    const result: IGetAnalyticsConnectionOptionsForWorkbookResult =
      await WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsConnectionOptionsForWorkbookRequest(
        {workbook_luid: workbookLuid}));

    return result.connectionMetadataList;
  }

  async function getHasScriptCalcs(workbookLuid: string) {
    let requestParams = {
      id: workbookLuid
    };
    let result = await WebHttpClientWithErrorToastInstance.sendRequest(new GetHasScriptCalcRequest(requestParams));
    return result as boolean;
  }

  async function getHasAnalyticsExtensionTable(workbookLuid: string) {
    let requestParams = {
      id: workbookLuid
    };
    let result = await WebHttpClientWithErrorToastInstance.sendRequest(new GetHasAnalyticsExtensionTableRequest(requestParams));
    return result as boolean;
  }

  function handleAnExtConnectionApply(connection: ListItem, workbookLuid: string, setAppliedConnection: (connectionApplied: ListItem) => void) {
    //If 'No Connection' is selected, then we want to unset.
    if (connection.value === 'none') {
      WebHttpClientWithErrorToastInstance.sendTagRequest((new RemoveAnalyticsExtConnectionForWorkbookRequest({workbook_luid: workbookLuid})));
      setAppliedConnection(null);
    } else {
      let connectionMapping: IAnalyticsConnectionMapping = {
        workbook_luid: workbookLuid,
        connection_luid: connection.value
      };
      WebHttpClientWithErrorToastInstance.sendTagRequest((new SetAnalyticsExtConnectionForWorkbookRequest(connectionMapping)));
      setAppliedConnection(connection);
    }
  }

  export function AnalyticsConnectionSettingsSection(props: PublishCompleteDialogProps) {
    // First two consts are results of API calls, are assigned this way so that promises can resolve fully.
    const usesAnalyticsExtensions = useWorkbookUsesAnalyticsExtensionsInitializer(props, getHasScriptCalcs, getHasAnalyticsExtensionTable);
    const connectionList = useAnExtConnectionListInitializer(props, getAnalyticsConnections, usesAnalyticsExtensions);
    // appliedConnection is the connection that has actually been applied, and an API call made.
    const [appliedConnection, setAppliedConnection] = React.useState<ListItem>(null);
    // onChange is the change handler for the connection dropdown.
    // selectedConnection is the currently selected (though maybe not applied) connection
    const {selectedConnection, onChange} = useChangeHandler(connectionList);

    let dropdownValue;
    // check list length of 2 because it will always contains a "No Analytics Extension" option
    if (connectionList.length < 2) {
      dropdownValue = Messages.publishWorkbookSuccessDialogAnExt_noConnectionsAvailable();
    } else if (!selectedConnection) {
      dropdownValue = Messages.publishWorkbookSuccessDialogAnExt_selectConnection();
    } else {
      dropdownValue = selectedConnection.value;
    }

    return (
      <div className="publish-complete-an-ext-section" style={{display: usesAnalyticsExtensions ? null : 'none'}}>
        <div style={{display: appliedConnection ? 'none' : null}} className="publish-complete-an-ext-no-connection-selected">
          <div className="tb-dialogs-warning "/>
          <div className="publish-complete-an-ext-no-connection-selected-text">
            {Messages.publishWorkbookSuccessDialogAnExt_noConnectorSelected()}
          </div>
        </div>
        <div className="publish-complete-an-ext-no-connection-warning">
          {Messages.publishWorkbookSuccessDialogAnExt_noConnectionWarning()}
        </div>
        <div className="publish-complete-an-ext-row" style={{display: connectionList.length > 0 ? 'none' : null}}>
          <ActivitySpinnerWidget
            shouldShowUnderlay={false}
            spinnerSize={ActivitySpinnerSize.Small}
            testId="tb-publish-complete-anext-workbook-options-loading-spinner"
          />
        </div>
        <div className="publish-complete-an-ext-row" style={{display: connectionList.length > 0 ? null : 'none'}}>
          <SingleSelectDropdown
            className="publish-complete-an-ext-dropdown"
            testId="tb-publish-complete-anext-workbook-options"
            label={Messages.publishWorkbookSuccessDialogAnExt_chooseAConnection()}
            list={<List items={connectionList}/>}
            onChange={onChange}
            type={DropdownButtonType.Outline}
            disabled={false}
            value={dropdownValue}
          />
          <ButtonWidget
            className="publish-complete-an-ext-apply-button"
            testId="tb-publish-complete-anext-workbook-apply-connection"
            buttonType={ButtonType.Outline}
            id="applyWorkbookConnection"
            disabled={connectionList.length < 2 || selectedConnection?.value === appliedConnection?.value}
            handleClick={() => {
              handleAnExtConnectionApply(selectedConnection, props.workbook.luid, setAppliedConnection);
            }}>{Messages.publishWorkbookSuccessDialogAnExt_applyConnection()}</ButtonWidget>
        </div>
      </div>
    );
  }

  export function DevicePreviewLink(props: PublishCompleteDialogProps) {
    if (!props.shouldDisplayDevicePreview) {
      return null;
    }

    return (
      <div className="tb-device-preview">
        <TextLinkWidget
          testId={'tb-device-preview-text'}
          className={'tb-device-preview-text'}
          onClick={props.onDevicePreviewClick}
          kind={TextLinkKind.LowEmphasis}
          tabIndex={0}
        >
          {Messages.publishWorkbookSuccessDialogText_devicePreviewMessage()}
        </TextLinkWidget>
      </div>
    );
  }

  export function DatasourceSection(props: PublishCompleteDialogProps) {
    if (props.datasources.length === 0 && props.shouldDisplayScheduleButton === false) {
      return null;
    }

    return (
      <div className="tb-datasources">
        <span className="tb-datasources-title">{Messages.publishWorkbookSuccessDialogText_datasourcesHeader()}</span>
        <div className="tb-datasources-body">
          {props.datasources.map((item, index) => <Datasource key={index} {...{name: item.name, datasourceIconClass: props.getDatasourceIconClass(item)}}/>)}
          <ScheduleRefreshLink {...props}/>
        </div>
      </div>
    );
  }

  export function Datasource(props: DatasourceProps): JSX.Element {
    const iconClass = `tb-published-icon ${props.datasourceIconClass}`;
    return (
      <div className="tb-published-item">
        <div className="tb-datasource-wrapper">
          <div className={iconClass} />
          <div className="tb-published-name">{props.name}</div>
        </div>
      </div>
    );
  }

  export function ScheduleRefreshLink(props: PublishCompleteDialogProps) {
    if (props.shouldDisplayScheduleButton === false) {
      return null;
    }

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/anchor-is-valid */
    return (
      <div className="tb-data-refresh">
        <div className="tb-data-refresh-text">{Messages.publishWorkbookSuccessDialogText_scheduleMessage()}</div>
        {/* Must call props.close here so that this ReactDialog's promise resolves on exit (instead of rejects) and the schedule dialog can open  */}
        {/* See how this dialog is created in PublishWorkbookAction.ts  */}
        <a className="tb-data-refresh-link" onClick={props.close}>{Messages.publishWorkbookSuccessDialogText_scheduleLink()}</a>
      </div>
    );
  }

  export function TableauBridgeBox(props: PublishCompleteDialogProps) {
    if (!props.shouldDisplayLiveQueryInfo) {
      return null;
    }

    return (
      <div className="tb-box tb-publish-details">
        <span>{props.publishLiveQueryInfo}</span>
        &nbsp;
        <Tooltip
          position={TooltipPosition.Right}
          getContent={() => <div>{Messages.publishDatasourceSuccessDialogText_onlineSync_help()}</div>}
        >
          <span style={{ textDecoration: 'underline' }}>
            {Messages.publishDatasourceSuccessDialogText_onlineSync_whatsthis()}
          </span>
        </Tooltip>
      </div>
    );
  }

  export function GetShareContentButtonProps(props: PublishCompleteDialogProps): ShareContentButtonProps {
    /***
    * Must call props.dismiss here so that this ReactDialog's promise rejects on exit (instead of resolves) to prevent the schedule dialog from opening
    * See how this dialog is created in PublishWorkbookAction.ts
    * */
    return {
      handleClick: () => {
        props.dismiss();
        props.shareContent(props.workbook);
      },
      text: Messages.publishWorkbookSuccessDialogButton_share(),
      testId: 'share-icon'
    };
  }

  function Footer(props: PublishCompleteDialogProps) {
    const containerInfo: ContainerInfoState = selectContainerInfo(getAppStore().getState());
    if (containerInfo.type === ContainerType.PersonalSpace) {
      return <div
        className="publish-complete-dialog-footer"
        data-tb-test-id="publish-complete-dialog-footer"
        >
        <>&nbsp;</>
        <DevicePreviewLink {...props} />
      </div>;
    } else {
      return <div
        className="publish-complete-dialog-footer"
        data-tb-test-id="publish-complete-dialog-footer"
        >
        <DialogFooterButtonsWidget buttons={[ ShareContentButton(GetShareContentButtonProps(props)) ]} testId="publish-complete-dialog-footer-buttons" />
        <DevicePreviewLink {...props} />
      </div>;
    }
  }

  const mapDispatchToProps: MapDispatchToProps<PublishCompleteDialogDispatchProps, PublishCompleteDialogOwnProps> = (dispatch) => bindActionCreators({
    shareContent: shareContent,
  }, dispatch);

  export const ConnectedFooter: React.ComponentClass<PublishCompleteDialogOwnProps> = connect(null, mapDispatchToProps)(Footer);
}
